import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Undo as ReturnIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Sale, SaleReturn, RefundMethod } from '../utils/types';
import { getAllSales, getAllReturns, createFullReturn, createPartialReturn } from '../services/posService';
import { formatCurrency } from '../utils/numberUtils';
import InvoiceDialog from '../components/pos/InvoiceDialog';
import ReturnDialog from '../components/pos/ReturnDialog';
import EnhancedReturnDialog from '../components/pos/EnhancedReturnDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const InvoicesPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState<Sale[]>([]);
  const [returns, setReturns] = useState<SaleReturn[]>([]);
  const [filteredSales, setFilteredSales] = useState<Sale[]>([]);
  const [filteredReturns, setFilteredReturns] = useState<SaleReturn[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'info' | 'warning'
  });

  // تحميل البيانات
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [salesData, returnsData] = await Promise.all([
        getAllSales(),
        getAllReturns()
      ]);
      
      setSales(salesData);
      setReturns(returnsData);
      setFilteredSales(salesData);
      setFilteredReturns(returnsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل البيانات',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // البحث والتصفية
  useEffect(() => {
    if (searchTerm) {
      const filtered = sales.filter(sale =>
        sale.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.employeeName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSales(filtered);

      const filteredRet = returns.filter(ret =>
        ret.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ret.saleId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ret.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredReturns(filteredRet);
    } else {
      setFilteredSales(sales);
      setFilteredReturns(returns);
    }
  }, [searchTerm, sales, returns]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewInvoice = (sale: Sale) => {
    setSelectedSale(sale);
    setInvoiceDialogOpen(true);
  };

  const handleShowReturn = (sale: Sale) => {
    setSelectedSale(sale);
    setReturnDialogOpen(true);
  };

  const handleShowReturnById = (saleId: string) => {
    const sale = sales.find(s => s.id === saleId);
    if (sale) {
      handleShowReturn(sale);
    }
  };

  const handleReturn = async (returnData: any) => {
    try {
      // معالجة الإرجاع
      await loadData(); // إعادة تحميل البيانات
      setSnackbar({
        open: true,
        message: 'تم إرجاع المنتجات بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error processing return:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء معالجة الإرجاع',
        severity: 'error'
      });
    }
  };

  // معالجة المرتجع الكامل (النظام الجديد)
  const handleFullReturn = async (saleId: string, reason: string, refundMethod: RefundMethod) => {
    try {
      if (!currentUser) throw new Error('المستخدم غير مسجل الدخول');

      await createFullReturn(
        saleId,
        reason,
        refundMethod,
        currentUser.id,
        currentUser.name || 'موظف',
        `ملاحظات: ${reason}`
      );

      // إعادة تحميل البيانات
      await loadData();

      setSnackbar({
        open: true,
        message: `تم إنشاء المرتجع الكامل بنجاح - ${refundMethod === RefundMethod.CASH ? 'استرداد نقدي' : 'رصيد للعميل'}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating full return:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إنشاء المرتجع الكامل',
        severity: 'error'
      });
      throw error;
    }
  };

  // معالجة المرتجع الجزئي (النظام الجديد)
  const handlePartialReturn = async (saleId: string, items: any[], reason: string, refundMethod: RefundMethod) => {
    try {
      if (!currentUser) throw new Error('المستخدم غير مسجل الدخول');

      // تحويل العناصر إلى التنسيق المطلوب
      const returnItems = items.map(item => ({
        itemId: item.itemId,
        name: item.name,
        sku: item.sku || '',
        quantity: item.returnQuantity,
        price: item.price,
        total: item.price * item.returnQuantity,
        warehouseId: item.warehouseId,
        returnReason: item.returnReason
      }));

      await createPartialReturn(
        saleId,
        returnItems,
        reason,
        refundMethod,
        currentUser.id,
        currentUser.name || 'موظف',
        `ملاحظات: ${reason}`
      );

      // إعادة تحميل البيانات
      await loadData();

      setSnackbar({
        open: true,
        message: `تم إنشاء المرتجع الجزئي بنجاح - ${refundMethod === RefundMethod.CASH ? 'استرداد نقدي' : 'رصيد للعميل'}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating partial return:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إنشاء المرتجع الجزئي',
        severity: 'error'
      });
      throw error;
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'partiallyPaid': return 'warning';
      case 'unpaid': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case 'paid': return 'مدفوع';
      case 'partiallyPaid': return 'مدفوع جزئياً';
      case 'unpaid': return 'غير مدفوع';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* العنوان الرئيسي */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        📋 الفواتير والمرتجعات
      </Typography>

      {/* شريط البحث */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="البحث في الفواتير والمرتجعات..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* التبويبات */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
          <Tab 
            label={`الفواتير (${filteredSales.length})`} 
            icon={<ReceiptIcon />}
            iconPosition="start"
          />
          <Tab 
            label={`المرتجعات (${filteredReturns.length})`} 
            icon={<ReturnIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* محتوى التبويبات */}
      <TabPanel value={tabValue} index={0}>
        {/* جدول الفواتير */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>رقم الفاتورة</TableCell>
                <TableCell>التاريخ</TableCell>
                <TableCell>العميل</TableCell>
                <TableCell>المبلغ</TableCell>
                <TableCell>حالة الدفع</TableCell>
                <TableCell>حالة المرتجعات</TableCell>
                <TableCell>الموظف</TableCell>
                <TableCell>الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSales.map((sale) => (
                <TableRow key={sale.id}>
                  <TableCell>{sale.invoiceNumber || sale.id.substring(0, 8)}</TableCell>
                  <TableCell>
                    {new Date(sale.createdAt).toLocaleDateString('ar-SA')}
                  </TableCell>
                  <TableCell>{sale.customerName || 'عميل عام'}</TableCell>
                  <TableCell>{formatCurrency(sale.grandTotal)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getPaymentStatusText(sale.paymentStatus)}
                      color={getPaymentStatusColor(sale.paymentStatus)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={sale.returnStatus === 'FULL_RETURNED' ? 'مرتجع كلياً' :
                            sale.returnStatus === 'PARTIAL_RETURNED' ? 'مرتجع جزئياً' :
                            'لم يتم الإرجاع'}
                      color={sale.returnStatus === 'FULL_RETURNED' ? 'error' :
                             sale.returnStatus === 'PARTIAL_RETURNED' ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{sale.employeeName}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleViewInvoice(sale)}
                      color="primary"
                      size="small"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleShowReturn(sale)}
                      color="warning"
                      size="small"
                      disabled={sale.returnStatus === 'FULL_RETURNED'}
                      title={sale.returnStatus === 'FULL_RETURNED' ? 'الفاتورة مرتجعة كلياً بالفعل' : 'إنشاء مرتجع'}
                    >
                      <ReturnIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* جدول المرتجعات */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>رقم المرتجع</TableCell>
                <TableCell>رقم الفاتورة</TableCell>
                <TableCell>التاريخ</TableCell>
                <TableCell>العميل</TableCell>
                <TableCell>المبلغ</TableCell>
                <TableCell>النوع</TableCell>
                <TableCell>الحالة</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReturns.map((returnItem) => (
                <TableRow key={returnItem.id}>
                  <TableCell>{returnItem.id.substring(0, 8)}</TableCell>
                  <TableCell>{returnItem.saleId.substring(0, 8)}</TableCell>
                  <TableCell>
                    {new Date(returnItem.createdAt).toLocaleDateString('ar-SA')}
                  </TableCell>
                  <TableCell>{returnItem.customerName || 'عميل عام'}</TableCell>
                  <TableCell>{formatCurrency(returnItem.totalAmount)}</TableCell>
                  <TableCell>
                    <Chip
                      label={returnItem.returnType === 'FULL' ? 'كامل' : 'جزئي'}
                      color={returnItem.returnType === 'FULL' ? 'error' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={returnItem.returnStatus === 'COMPLETED' ? 'مكتمل' : 'قيد المعالجة'}
                      color={returnItem.returnStatus === 'COMPLETED' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* حوار الفاتورة */}
      <InvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        sale={selectedSale}
        onReturn={handleShowReturnById}
        showReturnButton={true}
      />

      {/* حوار المرتجعات المحسن */}
      <EnhancedReturnDialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        sale={selectedSale}
        onFullReturn={handleFullReturn}
        onPartialReturn={handlePartialReturn}
      />

      {/* حوار المرتجعات القديم (احتياطي) */}
      {/* <ReturnDialog
        open={false}
        onClose={() => setReturnDialogOpen(false)}
        sale={selectedSale}
        onReturn={handleReturn}
      /> */}

      {/* رسائل التنبيه */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoicesPage;
